/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useMemo,
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import {
  Row,
  Select,
} from 'antd';
import {
  FileTextOutlined,
  SettingOutlined,
} from '@ant-design/icons';

import WorkflowActionNode from './WorkflowActionNode';
import WorkflowSimpleMappingConfigureDrawer from './WorkflowSimpleMappingConfigureDrawer';
import WorkflowHandle from './WorkflowHandle';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import useToggle from '../../common/hooks/useToggle';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';
import { updateData, getType } from './workflowHelpers';

const HOVER_TEXT = 'This step can add a certification to a user based on the fields submitted.';
const DISABLED_TEXT = 'Add at least one Text Field, Custom Choose One or More field, or Static Text field to enable this step OR a preconfigured certification type';

const CERTIFICATION_FIELDS = [
  {
    key: 'user',
    text: 'User',
    dataType: 'Users',
    types: ['dropdown - Users', 'formAuthor'],
  },
  {
    key: 'issuer',
    text: 'Issuer',
    types: ['text'],
  },
  {
    key: 'acquiredDate',
    text: 'Acquired Date',
    types: ['dateTime'],
  },
  {
    key: 'expiryDate',
    text: 'Expiry Date',
    types: ['dateTime'],
  },
  {
    key: 'files',
    text: 'Files',
    types: ['attachment'],
  },
];

const TYPE_MAP_SET = new Set([
  'text',
  'dropdown - Custom',
  'staticText',
]);

export default function WorkflowCreateCertificationNode({
  isDisplay,
  setElements,
  setDataMap,
  name,
  sections = [],
  setDisableNext,
} = {}) {
  return function _({
    id,
    draggable,
    data = {},
    disabled,
  }) {
    const { isToggled, toggle } = useToggle();

    const {
      fieldMappings: initialFieldMappings = {},
      attachFormPDF: initialAttachFormPDF = false,
      certificationTypeId: initialCertificationTypeId,
    } = data;

    const title = `Create Certification${disabled ? ' - DISABLED' : ''}`;

    const [selectedFieldMappings, setSelectedFieldMappings] = useState(initialFieldMappings);
    const [attachFormPDF, setAttachFormPDF] = useState(initialAttachFormPDF);
    const [selectedCertificationTypeId, setSelectedCertificationTypeId] = useState(
      initialCertificationTypeId ?? initialFieldMappings?.type,
    );

    const {
      users: {
        certificationCustomFieldTemplate: {
          sections: customFieldSections = [],
        } = {},
        certificationTypes: userCertificationTypes = [],
      } = {},
    } = useSelector((state) => state.certifications);

    const customFields = useMemo(() => {
      const fields = [];
      customFieldSections.forEach((section) => {
        const { fields: sectionFields = [], name: sectionName } = section;
        sectionFields.forEach((field) => {
          const {
            configProps: {
              title: fieldTitle,
              dataType: fieldDataType,
              optional = false,
            } = {},
            selectedType: fieldType,
            id: fieldId,
          } = field;

          fields.push({
            key: fieldId,
            text: `${sectionName} - ${fieldTitle}`,
            type: fieldType,
            dataType: fieldDataType,
            required: !optional,
          });
        });
      });
      return fields;
    }, [customFieldSections]);

    const requiredFields = useMemo(() => {
      const fields = new Set(['user']);
      customFields.forEach((field) => {
        if (field.required) {
          fields.add(field.key);
        }
      });
      return fields;
    }, [customFields]);

    const errorDetails = useMemo(() => ({
      content: (
        <div style={{ maxWidth: 300 }}>
          This step requires <b>Type</b> and <b>User</b> to be configured.
          {customFields.some((field) => field.required) && (
            <>
              <div style={{ marginTop: 8 }}>
                The following custom fields are also required:
              </div>
              <ul style={{ marginTop: 4, marginBottom: 0 }}>
                {customFields.filter((field) => field.required).map((field) => (
                  <li key={field.key}><b>{field.text}</b></li>
                ))}
              </ul>
            </>
          )}
        </div>
      ),
    }), [customFields]);

    const allFields = useMemo(() => [
      ...CERTIFICATION_FIELDS,
      ...customFields,
    ], [customFields]);

    const disableNext = useMemo(() => {
      const isTypeSet = !!selectedCertificationTypeId;
      const isUserSet = selectedFieldMappings?.user;

      if (!isTypeSet || !isUserSet) return true;

      // Check if all required custom fields are mapped
      const requiredCustomFields = customFields.filter((field) => field.required);
      const allRequiredCustomFieldsMapped = requiredCustomFields.every(
        (field) => selectedFieldMappings?.[field.key],
      );

      return !allRequiredCustomFieldsMapped;
    }, [
      selectedCertificationTypeId,
      selectedFieldMappings,
      customFields,
    ]);

    useEffect(() => {
      if (!setDisableNext) return;

      setDisableNext((prev) => ({ ...prev, [id]: disableNext }));
    }, [disableNext, setDisableNext, id]);

    const onDataChange = useCallback((key) => (newValue) => {
      if (!setDataMap || !id) return;
      setSelectedFieldMappings(newValue);
      setDataMap((dataMap) => ({
        ...dataMap,
        [id]: { ...dataMap[id], [key]: newValue },
      }));
    }, [id, setDataMap]);

    const onAttachFormPDFChange = useCallback((checked) => {
      if (!setDataMap || !id) return;
      setAttachFormPDF(checked);
      setDataMap((dataMap) => ({
        ...dataMap,
        [id]: { ...dataMap[id], attachFormPDF: checked },
      }));
    }, [id, setDataMap]);

    const onCertificationTypeChange = useCallback((newCertificationTypeId) => {
      if (!setDataMap || !id) return;
      setSelectedCertificationTypeId(newCertificationTypeId);
      setDataMap(updateData(id, { certificationTypeId: newCertificationTypeId }));
    }, [setDataMap, id]);

    const sourceSections = useMemo(() => (
      [{
        name: 'Form Data',
        fields: [
          {
            id: 'formNumber',
            configProps: {
              title: 'Form Number',
            },
            selectedType: 'text',
          },
          {
            id: 'formAuthor',
            configProps: {
              title: 'Form Author',
            },
            selectedType: 'formAuthor',
          },
        ],
      }].concat(sections)
    ), [sections]);

    const certificationTypeOptions = useMemo(() => {
      const opts = [];
      userCertificationTypes?.forEach((certificationType) => {
        opts.push({
          label: certificationType.name,
          value: certificationType.id,
        });
      });
      sourceSections.forEach((section) => {
        section?.fields?.forEach((field) => {
          if (TYPE_MAP_SET.has(getType(field))) {
            opts.push({
              label: `${section.name} - ${field.configProps?.title}`,
              value: field.id,
            });
          }
        });
      });
      return opts;
    }, [userCertificationTypes, sourceSections]);

    return (
      <WorkflowActionNode
        id={id}
        title={title}
        type="createCertification"
        Icon={FileTextOutlined}
        draggable={draggable}
        isDisplay={isDisplay || disabled}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
        style={disabled ? { opacity: 0.7 } : {}}
        errorPopover={disableNext ? errorDetails : null}
      >
        {!draggable && (
          <div>
            <Row style={{ marginTop: 10 }}>
              Select Type
            </Row>
            <Row style={{ margin: '10px 0px' }}>
              <Select
                onChange={onCertificationTypeChange}
                value={selectedCertificationTypeId}
                showSearch
                optionFilterProp="label"
                options={certificationTypeOptions}
              />
            </Row>
            <OnTraccrCheckbox
              style={{ marginTop: 5 }}
              onChange={onAttachFormPDFChange}
              defaultChecked={initialAttachFormPDF}
              value={attachFormPDF}
              label="Attach Form PDF?"
              hoverNode={(
                <div style={{ width: 300 }}>
                  Check this box if you want the form PDF to be attached to the certification.
                </div>
              )}
            />
            <Row style={{ margin: '20px 0px' }}>
              <BorderlessButton
                title="Configure"
                style={{ margin: 5 }}
                iconNode={<SettingOutlined />}
                onClick={toggle}
              />
            </Row>
            <WorkflowSimpleMappingConfigureDrawer
              id={id}
              visible={isToggled}
              onClose={toggle}
              onSubmit={toggle}
              sourceName={name}
              sourceSections={sourceSections}
              fieldMappings={selectedFieldMappings}
              onFieldMappingsChange={onDataChange('fieldMappings')}
              data={data}
              setDataMap={setDataMap}
              fields={allFields}
              requiredFields={requiredFields}
            />
            <WorkflowHandle type="target" position="top" disabled={isDisplay} />
          </div>
        )}
      </WorkflowActionNode>
    );
  };
}
